// src/ai/gemini.ts
import { ai } from '../../firebase/config';               // <-- your getAI(app) export
import { getGenerativeModel } from 'firebase/ai';
import { getModelForFeature, getFallbackModelForFeature, isSchoolAIPaused } from './aiConfigService';
import { logAIUsage } from './aiUsageLogger';
import type {
  GenerationParams,
  Question,
  ResearchResult,
  GroundingChunk,
  BookDetails,
} from './types';

/* -------------------------------------------------------------------------- */
/*  1. Model instance – no API key, uses Firebase service-account auth      */
/* -------------------------------------------------------------------------- */
// We no longer hardcode fallbacks, they are fetched from configuration
let currentModelName = 'gemini-1.5-flash';
if (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_GEMINI_MODEL) {
  currentModelName = (import.meta as any).env.VITE_GEMINI_MODEL as string;
}

let model = getGenerativeModel(ai, { model: currentModelName });

// Initialize model with configuration from Firestore
async function initializeModel(
  feature: 'research' | 'examGeneration' | 'libraryManagement' | 'admissionPattern' | 'chat' | 'audioAssistant'
        | 'independent_research' | 'independent_examGeneration' | 'independent_audioAssistant',
  schoolId?: string
) {
  const configuredModel = await getModelForFeature(feature as any, schoolId);
  currentModelName = configuredModel;
  model = getGenerativeModel(ai, { model: configuredModel });
  console.info(`Initialized ${feature} with model: ${configuredModel}`);
}

async function switchToNextModel(feature: any) {
  const fallbackModel = await getFallbackModelForFeature(feature);
  if (currentModelName === fallbackModel) return false;
  
  currentModelName = fallbackModel;
  console.warn(`Switching Gemini model to configured fallback: ${fallbackModel}`);
  model = getGenerativeModel(ai, { model: fallbackModel });
  return true;
}

/* -------------------------------------------------------------------------- */
/*  2. Helper – fire a prompt and return the raw text (or JSON string)      */
/* -------------------------------------------------------------------------- */

// Convert Blob to Base64
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result as string;
      // Remove data URL prefix
      resolve(base64data.split(',')[1]);
    };
    reader.onerror = reject;
  });
};

export async function geminiGenerate(prompt: string) {
  return geminiMultimodalGenerate(prompt, null);
}

export async function geminiMultimodalGenerate(
  prompt: string,
  audioBlob: Blob | null,
  schoolId?: string,
  feature: 'audioAssistant' | 'independent_audioAssistant' = 'audioAssistant'
) {
  // Only initialize the audio model when audio is actually being used.
  // For plain text calls (audioBlob=null), the caller (performResearch, generateQuestions, etc.)
  // is responsible for calling initializeModel() with the correct feature before calling geminiGenerate().
  if (audioBlob) {
    await initializeModel(feature, schoolId);
  }

  const maxAttempts = 2;
  let lastErr: any = null;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      console.info(`geminiMultimodalGenerate: attempting generation with model=${currentModelName}`);

      const parts: any[] = [];
      if (audioBlob) {
        const base64Audio = await blobToBase64(audioBlob);
        parts.push({
          inlineData: {
            mimeType: audioBlob.type || 'audio/webm',
            data: base64Audio
          }
        });
        parts.push({ text: "Please listen to this audio query and respond. " + (prompt || "") });
      } else {
        parts.push({ text: prompt });
      }

      const result = await model.generateContent(parts);
      const response = await result.response;
      const text = response.text();
      if (!text) throw new Error('Empty response from Gemini');

      return text;
    } catch (err: any) {
      lastErr = err;
      const msg = String(err?.message || err);
      // Detect retired-model errors, model-not-found, or unsupported-method guidance from Firebase
      const lower = msg.toLowerCase();
      const shouldSwitch =
        msg.includes('RETIRED_MODEL') ||
        lower.includes('retired') ||
        lower.includes('not found') ||
        lower.includes('not supported for generatecontent') ||
        lower.includes('is not found') ||
        lower.includes('listmodels') ||
        lower.includes('404');

      if (shouldSwitch) {
        const switched = await switchToNextModel(feature);
        if (switched) {
          console.warn('Model unsupported or retired; switching to configured fallback model and retrying...');
          continue; // retry with next model
        }
      }
      // Not a RETIRED_MODEL or no fallback available — rethrow
      throw err;
    }
  }
  // if we exit loop without returning, throw last error
  throw lastErr || new Error('Gemini generation failed');
}

// Helper to clean up formatted text (Markdown, LaTeX) into plain readable text
function cleanFormattedText(text: string): string {
  let cleaned = String(text || '');

  // 1. Clean up markdown-style formatting
  cleaned = cleaned
    // Remove bold/italic markers
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    // Remove backticks
    .replace(/`([^`]+)`/g, '$1')
    // Clean up LaTeX block math (keep content, remove $$)
    .replace(/\$\$([\s\S]+?)\$\$/g, '$1')
    // Clean up LaTeX inline math (keep content, remove single $)
    .replace(/\$([^\$]+?)\$/g, '$1')
    // Remove markdown headers
    .replace(/^#+\s+/gm, '')
    // Clean up lists
    .replace(/^[-*+]\s+/gm, '• ')
    // Remove HTML tags but keep content
    .replace(/<[^>]+>/g, '')
    // Fix multiple newlines
    .replace(/\n{3,}/g, '\n\n')
    // Fix multiple spaces
    .replace(/[ \t]+/g, ' ');

  // 2. Clean up mathematical notation and convert to proper symbols
  cleaned = cleaned
    // First clean up any plain text mathematical terms
    .replace(/\btheta\b/g, 'θ')
    .replace(/\bpi\b/g, 'π')
    .replace(/\bsqrt\b/g, '√')
    .replace(/\balpha\b/g, 'α')
    .replace(/\bbeta\b/g, 'β')
    .replace(/\bgamma\b/g, 'γ')
    .replace(/\bdelta\b/g, 'δ')
    .replace(/\bepsilon\b/g, 'ε')
    .replace(/\binfinity\b/g, '∞')
    .replace(/\bsquare (\w+)\b/g, '$1²')
    .replace(/\bcubic (\w+)\b/g, '$1³')

    // Convert LaTeX exponents to proper superscript numbers
    .replace(/\^2\b/g, '²')
    .replace(/\^3\b/g, '³')
    .replace(/\^4\b/g, '⁴')
    .replace(/\^5\b/g, '⁵')
    .replace(/\^6\b/g, '⁶')
    .replace(/\^7\b/g, '⁷')
    .replace(/\^8\b/g, '⁸')
    .replace(/\^9\b/g, '⁹')
    // Handle negative exponents
    .replace(/\^-2\b/g, '⁻²')
    .replace(/\^-3\b/g, '⁻³')
    .replace(/\^-1\b/g, '⁻¹')
    // Keep other exponents in neat form
    .replace(/\^(\d+)\b/g, '^$1')

    // Convert LaTeX commands to actual mathematical symbols
    .replace(/\\sqrt\{([^}]+)\}/g, '√($1)')  // Convert \sqrt{x} to √(x)
    .replace(/\\sqrt/g, '√')  // Handle standalone sqrt
    .replace(/\\pi\b/g, 'π')  // Convert \pi to π
    .replace(/\\theta\b/g, 'θ')  // Convert \theta to θ
    .replace(/\\alpha\b/g, 'α')  // Convert \alpha to α
    .replace(/\\beta\b/g, 'β')  // Convert \beta to β
    .replace(/\\gamma\b/g, 'γ')  // Convert \gamma to γ
    .replace(/\\delta\b/g, 'δ')  // Convert \delta to δ
    .replace(/\\epsilon\b/g, 'ε')  // Convert \epsilon to ε
    .replace(/\\infty\b/g, '∞')  // Convert \infty to ∞
    .replace(/\\sum\b/g, '∑')  // Convert \sum to ∑
    .replace(/\\prod\b/g, '∏')  // Convert \prod to ∏
    .replace(/\\int\b/g, '∫')  // Convert \int to ∫
    .replace(/\\times\b/g, '×')  // Convert \times to ×
    .replace(/\\div\b/g, '÷')  // Convert \div to ÷
    .replace(/\\pm\b/g, '±')  // Convert \pm to ±
    .replace(/\\mp\b/g, '∓')  // Convert \mp to ∓
    .replace(/\\leq\b/g, '≤')  // Convert \leq to ≤
    .replace(/\\geq\b/g, '≥')  // Convert \geq to ≥
    .replace(/\\neq\b/g, '≠')  // Convert \neq to ≠
    .replace(/\\approx\b/g, '≈')  // Convert \approx to ≈
    .replace(/\\cdot\b/g, '·')  // Convert \cdot to ·
    .replace(/\\degree\b/g, '°')  // Convert \degree to °

    // Fix spacing around mathematical operators
    .replace(/(\d)([+\-×÷=<>])/g, '$1 $2')
    .replace(/([+\-×÷=<>])(\d)/g, '$1 $2')

    // Clean up multiple spaces but preserve newlines for formatting
    .replace(/[ \t]+/g, ' ')
    // Fix complex numbers
    .replace(/\\i\b/g, 'i')  // Keep complex i
    // Handle e raised to power specially (for Euler's form)
    .replace(/e\^{([^}]+)}/g, 'e^($1)')
    // Fix fractions to proper division format
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1/$2)')
    // Add commas between numbers in coordinate pairs: (10 4) → (10, 4)
    .replace(/\((\s*-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)/g, '($1, $2)')
    // Add commas between multiple coordinates: (1 2) (3 4) → (1, 2), (3, 4)
    .replace(/\)\s*\(/g, '), (')
    // Fix spaces around mathematical operators while preserving them
    .replace(/(\d)([+\-×÷=])/g, '$1 $2')  // Add space after number before operator
    .replace(/([+\-×÷=])(\d)/g, '$1 $2')  // Add space after operator before number
    // Preserve mathematical operators
    .replace(/-/g, '−')  // Use proper minus sign
    .replace(/\*/g, '×')  // Use proper multiplication sign
    // Fix spacing but preserve multiple newlines for options
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return cleaned;
}

// Helper to clean up JSON content recursively
function cleanJsonContent(obj: any): any {
  if (typeof obj === 'string') {
    return cleanFormattedText(obj);
  }
  if (Array.isArray(obj)) {
    return obj.map(item => cleanJsonContent(item));
  }
  if (obj && typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      cleaned[key] = cleanJsonContent(value);
    }
    return cleaned;
  }
  return obj;
}

// Robust JSON extractor used across multiple generator helpers.
// Tries direct parse, fenced-code extraction, then balanced-brace extraction.
function parseJsonFromText(text: string) {
  // Normalize and try multiple extraction strategies. Return parsed JSON or throw.
  let t = String(text || '');
  // remove BOM and trim
  t = t.replace(/^\uFEFF/, '').trim();

  let parsed: any = null;

  // 1) direct parse
  try {
    parsed = JSON.parse(t);
    return cleanJsonContent(parsed);
  } catch (e) {
    // continue
  }

  // 2) try unescaped HTML entities (some responses come HTML-escaped)
  const unescapeHtml = (s: string) => s.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#39;/g, "'");
  try {
    const u = unescapeHtml(t);
    if (u !== t) {
      try {
        parsed = JSON.parse(u);
        return cleanJsonContent(parsed);
      } catch (e) { /* continue */ }
    }
  } catch (e) {
    // ignore
  }

  // 3) extract fenced code blocks (``` or ~~~) and try each inner block
  const fenceRegex = /(?:```|~~~)(?:json)?\s*([\s\S]*?)\s*(?:```|~~~)/ig;
  let m: RegExpExecArray | null;
  while ((m = fenceRegex.exec(t)) !== null) {
    const inner = (m[1] || '').trim();
    if (!inner) continue;
    try {
      parsed = JSON.parse(inner);
      return cleanJsonContent(parsed);
    } catch (e) {
      // try candidate starting from first brace
      const first = inner.search(/[\{\[]/);
      if (first >= 0) {
        const candidate = inner.slice(first);
        try {
          parsed = JSON.parse(candidate);
          return cleanJsonContent(parsed);
        } catch (_) { }
        // try removing trailing commas
        const fixed = candidate.replace(/,\s*(?=[}\]])/g, '');
        try {
          parsed = JSON.parse(fixed);
          return cleanJsonContent(parsed);
        } catch (_) { }
      }
    }
  }

  // 4) Balanced-scan: find any balanced { ... } or [ ... ] substring and try to parse it.
  const startChars = ['{', '['];
  for (let pos = 0; pos < t.length; pos++) {
    if (startChars.includes(t[pos])) {
      const open = t[pos];
      const close = open === '{' ? '}' : ']';
      let depth = 0;
      for (let i = pos; i < t.length; i++) {
        const ch = t[i];
        if (ch === open) depth += 1;
        else if (ch === close) depth -= 1;
        if (depth === 0) {
          const candidate = t.slice(pos, i + 1);
          try {
            parsed = JSON.parse(candidate);
            return cleanJsonContent(parsed);
          } catch (e) {
            // try to repair common issues like trailing commas
            const repaired = candidate.replace(/,\s*(?=[}\]])/g, '');
            try {
              parsed = JSON.parse(repaired);
              return cleanJsonContent(parsed);
            } catch (e2) {
              // continue searching
              break;
            }
          }
        }
      }
    }
  }

  // 5) As a last attempt, try to extract by regex matching a top-level JSON object/array
  const looseMatch = t.match(/([\{\[])[\s\S]*[\}\]]/);
  if (looseMatch) {
    const candidate = looseMatch[0];
    try {
      parsed = JSON.parse(candidate);
      return cleanJsonContent(parsed);
    } catch (e) {
      const repaired = candidate.replace(/,\s*(?=[}\]])/g, '');
      try {
        parsed = JSON.parse(repaired);
        return cleanJsonContent(parsed);
      } catch (e2) { }
    }
  }

  throw new Error('Failed to parse JSON from Gemini response');
}

/* -------------------------------------------------------------------------- */
/*  3. Mock fallback (kept identical to your original code)                 */
/* -------------------------------------------------------------------------- */
const generateMockQuestions = (params: GenerationParams, _content?: string): Question[] => {
  const questions: Question[] = [];
  const topicsString = params.topics.join(', ');

  params.sections.forEach(section => {
    for (let i = 0; i < section.numQuestions; i++) {
      const isMCQ =
        (section as any).isMCQ === true ||
        String((section as any).questionType) === 'MCQ';
      const numOptions =
        typeof (section as any).numOptions === 'number' && (section as any).numOptions > 0
          ? (section as any).numOptions
          : 4;
      const options = isMCQ
        ? Array.from({ length: numOptions }, (_, j) => `This is option ${String.fromCharCode(65 + j)}`)
        : undefined;

      const questionText = isMCQ
        ? `${i + 1}. This is a mock "MCQ question with ${numOptions} options" for the topic of ${topicsString}. This section is worth ${section.marks} marks. It might include a diagram like this: <svg width="100" height="100"><circle cx="50" cy="50" r="40" stroke="black" stroke-width="3" fill="red" /></svg>\nOptions:\n${options
          ?.map((opt, idx) => `${String.fromCharCode(65 + idx)}. ${opt}`)
          .join('\n')}`
        : `${i + 1}. This is a mock "${section.questionType}" question for the topic of ${topicsString}. This section is worth ${section.marks} marks. It might include a diagram like this: <svg width="100" height="100"><circle cx="50" cy="50" r="40" stroke="black" stroke-width="3" fill="red" /></svg>`;

      const q: any = {
        id: `mock-${Date.now()}-${section.name}-${i}`,
        section: section.name,
        subject: params.subject,
        grade: 'N/A',
        topic: topicsString,
        difficulty: params.difficulty,
        type: isMCQ ? 'MCQ' : section.questionType,
        question: questionText,
        answer: isMCQ ? 'A' : `This is the mock answer for question ${i + 1} in ${section.name}.`,
        explanation: `This mock explanation details why the answer for question ${i + 1} is correct.`,
      };
      if (isMCQ) q.options = options;
      questions.push(q as Question);
    }
  });
  return questions;
};

/* -------------------------------------------------------------------------- */
/*  4. generateQuestions – uses Firebase Gemini (no key)                     */
/* -------------------------------------------------------------------------- */
export const generateQuestions = async (
  params: GenerationParams,
  content?: string,
  schoolId?: string,
  isIndependent?: boolean
): Promise<Question[]> => {
  // Initialize model with exam generation configuration
  const featureKey = isIndependent ? 'independent_examGeneration' : 'examGeneration';
  await initializeModel(featureKey, schoolId);

  // ---------------------------------------------------------------------- //
  //  Build the same prompt you had before (kept 100% identical)           //
  // ---------------------------------------------------------------------- //
  const topicsString = params.topics.join(', ');
  const contentContext = content
    ? `Your primary source of information MUST be the following document content. Base all questions and answers strictly on this text. Do not use external knowledge.
--- DOCUMENT CONTENT START ---
${content}
--- DOCUMENT CONTENT END ---`
    : `You are to generate questions based on the topic(s) provided, using your general knowledge.`;

  const sectionPrompts = params.sections
    .map(section => {
      const isMCQ =
        (section as any).isMCQ === true ||
        String((section as any).questionType) === 'MCQ';
      let questionTypeDesc = '';
      if (isMCQ) {
        const optionsCount =
          typeof (section as any).numOptions === 'number' && (section as any).numOptions > 0
            ? (section as any).numOptions
            : 4;
        questionTypeDesc = `multiple-choice questions (MCQ). 
- Each question MUST have exactly ${optionsCount} options.
- The 'type' field in the JSON for these questions MUST be "MCQ".
- The 'options' field MUST be an array of strings containing the text for each option. Do not include labels like "A." in the option strings themselves.
- The 'answer' field MUST be only the letter of the correct option (e.g., "C").`;
      } else {
        questionTypeDesc =
          section.questionType === 'Short Answer'
            ? 'short-answer, objective-style questions'
            : 'longer, essay-style questions requiring detailed explanations or arguments';
      }

      const sectionStyle = (section as any).questionStyle || 'both';
      let styleInstruction = '';
      if (sectionStyle === 'single') {
        styleInstruction = '  - **Question Style: SINGLE QUESTIONS ONLY** - No sub-parts (a), (b), (c). Each question stands alone.';
      } else if (sectionStyle === 'sub-questions') {
        styleInstruction = '  - **Question Style: SUB-QUESTIONS** - Each question MUST have sub-parts (a), (b), (c), etc. Use natural intro like "Define the following:" or "Find the following:"';
      } else {
        styleInstruction = '  - **Question Style: MIXED** - Use both single questions and questions with sub-parts as appropriate.';
      }

      return `
- For ${section.name.toUpperCase()}:
  - **CRITICAL: Generate EXACTLY ${section.numQuestions} question(s) for this section. This is NON-NEGOTIABLE.**
  - The questions should be ${questionTypeDesc}.
  - **CRITICAL MARKS REQUIREMENT**: The total marks for this section MUST be EXACTLY ${section.marks} marks. This is NON-NEGOTIABLE.
${styleInstruction}
`;
    })
    .join('');

  const totalQuestions = params.sections.reduce((a, s) => a + s.numQuestions, 0);
  const customInstructionsContext = params.customInstructions
    ? `Additionally, adhere to these specific instructions from the teacher: "${params.customInstructions}"`
    : '';

  // Generate difficulty-specific instructions
  const getDifficultyInstructions = (difficulty: string): string => {
    switch (difficulty) {
      case 'Easy':
        return `
**CRITICAL DIFFICULTY LEVEL: EASY**
You MUST strictly adhere to these EASY difficulty guidelines:

1. **Cognitive Level**: Questions should test RECALL and BASIC UNDERSTANDING only (Bloom's Taxonomy levels 1-2)
   - Direct recall of facts, definitions, and basic concepts
   - Simple one-step problems
   - Recognition and identification tasks
   - Basic calculations with whole numbers or simple fractions
   
2. **Question Structure**:
   - Single-concept questions only (test ONE idea at a time)
   - NO multi-step problems or complex scenarios
   - Clear, straightforward wording without tricks or ambiguity
   - For MCQ: Distractors should be obviously wrong to students who studied
   
3. **Content Requirements**:
   - Use simple, everyday vocabulary
   - Include only foundational/introductory topics
   - Avoid edge cases, exceptions, or advanced applications
   - Calculations should involve simple numbers (ideally single or double digits)
   - For math: Problems should require 1-2 steps maximum
   
4. **Examples of EASY questions**:
   - "What is the formula for the area of a rectangle?"
   - "Calculate: 5 + 3 × 2"
   - "Define photosynthesis"
   - "Name the capital city of Kenya"

5. **AVOID for EASY**:
   - Word problems requiring interpretation
   - Questions requiring comparison or analysis
   - Multi-step calculations
   - Abstract or theoretical concepts
   - Application to real-world scenarios`;

      case 'Medium':
        return `
**CRITICAL DIFFICULTY LEVEL: MEDIUM**
You MUST strictly adhere to these MEDIUM difficulty guidelines:

1. **Cognitive Level**: Questions should test APPLICATION and BASIC ANALYSIS (Bloom's Taxonomy levels 3-4)
   - Application of concepts to new but straightforward situations
   - Simple problem-solving requiring 2-4 steps
   - Basic comparison and differentiation tasks
   - Understanding relationships between concepts
   
2. **Question Structure**:
   - Can involve 2-3 related concepts
   - Multi-step problems with clear progression
   - Include some word problems with moderate complexity
   - For MCQ: Distractors should be plausible but distinguishable with understanding
   
3. **Content Requirements**:
   - Use subject-appropriate vocabulary
   - Include standard curriculum topics at appropriate depth
   - May include some real-world applications
   - Calculations can involve decimals, fractions, and moderately complex numbers
   - For math: Problems should require 2-4 steps
   
4. **Examples of MEDIUM questions**:
   - "If a rectangle has length 8cm and width 5cm, what is its perimeter?"
   - "Explain how evaporation differs from boiling"
   - "Solve for x: 2x + 5 = 15"
   - "Describe TWO effects of deforestation on the environment"

5. **Balance for MEDIUM**:
   - Mix of straightforward and slightly challenging items
   - Some application but not complex analysis
   - Word problems should be clearly stated without excessive complexity`;

      case 'Hard':
        return `
**CRITICAL DIFFICULTY LEVEL: HARD**
You MUST strictly adhere to these HARD difficulty guidelines:

1. **Cognitive Level**: Questions should test ANALYSIS, EVALUATION, and SYNTHESIS (Bloom's Taxonomy levels 4-6)
   - Complex problem-solving requiring multiple concepts
   - Critical analysis and evaluation
   - Synthesis of information from different areas
   - Application to novel or complex real-world scenarios
   
2. **Question Structure**:
   - Multi-concept integration required
   - Complex multi-step problems (5+ steps for calculations)
   - Questions with sub-parts that build on each other
   - For MCQ: Distractors should be plausible and test deep understanding
   - May include "explain why" or "justify your answer" components
   
3. **Content Requirements**:
   - Use technical and subject-specific vocabulary appropriately
   - Include advanced topics and edge cases
   - Require synthesis of multiple concepts
   - Calculations may involve complex expressions, multiple variables
   - For math: Problems should require 5+ steps or creative problem-solving
   - Include scenarios that require critical thinking and inference
   
4. **Examples of HARD questions**:
   - "A ladder 10m long leans against a wall. If the foot is 6m from the wall, find the angle of elevation. Then calculate how far up the wall a person standing 2m up the ladder would be."
   - "Compare and contrast the effects of monetary and fiscal policy on inflation, providing specific examples from Kenya's economic history"
   - "Design an experiment to test the effect of pH on enzyme activity. Identify the independent, dependent, and controlled variables"
   - "Prove that the sum of angles in any triangle is 180°"

5. **Requirements for HARD**:
   - Questions should challenge even well-prepared students
   - Include analysis, comparison, or evaluation components
   - Require students to make connections between concepts
   - May include unfamiliar contexts requiring transfer of knowledge`;

      default:
        return '';
    }
  };

  const difficultyInstructions = getDifficultyInstructions(params.difficulty);

  const prompt = `
You are an expert curriculum developer specializing in creating high-quality exam papers for the Kenya Certificate of Secondary Education (KCSE). Your task is to generate exam questions in valid JSON format.

CRITICAL: You must return a valid JSON object with this exact structure:
{
  "questions": [
    {
      "section": "Section A",
      "question": "The actual question text...",
      "marks": 5,
      "type": "MCQ",
      "difficulty": "${params.difficulty}",
      "answer": "A",
      "explanation": "Step-by-step solution...",
      "options": ["A. First option", "B. Second option", "C. Third option", "D. Fourth option"]
    }
  ]
}

Rules for valid response:
1. Must be pure JSON - no markdown, no text before or after
2. The 'questions' field must be an array with at least one question
3. Each question must have ALL required fields
4. MCQ questions must have exactly 4 options with letter prefixes
5. Use proper mathematical symbols (e.g., π, θ, √, ², m², cm³) instead of text (e.g., pi, theta, sqrt, squared, square meters, cubic cm).
6. No duplicate letters in options or answer fields

**=== MANDATORY CONFIGURATION (NON-NEGOTIABLE) ===**

The following settings have been configured by the teacher and MUST be respected EXACTLY:

- **Exam Title**: ${params.examTitle}
- **Exam Type**: ${params.examType}
- **Subject**: ${params.subject}
- **Topics**: ${params.topics.join(', ')}
- **Difficulty Level**: ${params.difficulty} (ALL questions MUST be this difficulty)
- **Total Marks**: ${params.totalMarks} (sum of all question marks MUST equal EXACTLY this number)
- **Number of Sections**: ${params.sections.length}

${sectionPrompts}

**VERIFICATION BEFORE OUTPUT**: 
1. Count total questions - must match section requirements
2. Sum all marks - must equal EXACTLY ${params.totalMarks}
3. Verify all questions are "${params.difficulty}" difficulty
4. Verify subject is "${params.subject}"

${contentContext}

${customInstructionsContext}


CRITICAL INSTRUCTIONS FOR CLEAR, TEACHER-FRIENDLY OUTPUT:

1. Write all questions and answers in simple, clear language:
   - Use everyday terms that all teachers can understand
   - Break down complex mathematics into simple steps
   - Avoid confusing mathematical symbols
   - Write explanations in complete sentences

2. For mathematical expressions:
   - Write equations in standard, readable form
   - Use proper mathematical symbols for units (e.g., cm², m³, units²). Use superscripts for powers (e.g., x², y³) instead of words like "squared" or "cubed".
   - Show each calculation step clearly
   - Include clear diagrams where needed

3. Format the marking scheme clearly:
   - **MAXIMUM 5 STEPS PER ANSWER** - Never exceed 5 steps
   - Each step must be concise and clear
   - Show marks for each part clearly [e.g., Step 1 (2 marks)]
   - Use simple language in explanations

For questions requiring diagrams, generate clear, simple SVG diagrams that illustrate the concept without unnecessary complexity.

Generate ${totalQuestions} total questions based on the following criteria:
- Subject: ${params.subject}
- Topic(s): ${topicsString}
- Difficulty Level: ${params.difficulty}

${difficultyInstructions}

**ENFORCEMENT**: The difficulty level "${params.difficulty}" is MANDATORY. Every single question you generate MUST adhere strictly to the "${params.difficulty}" difficulty criteria defined above. If a question does not match the specified difficulty level, it will be rejected.

REMEMBER: Write everything so that ANY teacher can understand it, regardless of their mathematical background.

**CRITICAL: QUESTION STRUCTURE FORMAT**

**DO NOT use generic phrases like "Answer the following questions about [topic]:" - this sounds unnatural.**
Use NATURAL exam language like:
  - "Define the following:" then (a), (b), (c)...
  - "Find the following values:" then (a), (b)...
  - "Solve:" then (a), (b)...
  - Or just state context, then sub-parts

**CRITICAL ANSWER FORMAT RULES:**
1. The 'answer' field MUST contain the ACTUAL FINAL ANSWER, not "See steps below" or just "(a)"
2. For multi-part questions: "(a) [actual answer]\\n(b) [actual answer]"
3. NEVER put "See steps below" or "See sub-parts" in the answer field

**CRITICAL EXPLANATION FORMAT RULES:**
1. The 'explanation' field MUST contain step-by-step working
2. **MAXIMUM 5 STEPS** - combine steps if needed to stay within 5
3. Format: "(a) Step 1: ... Step 2: ... (max 5)\\n(b) Step 1: ... (max 5)"
4. Each step must show actual calculations/reasoning, not just describe what to do

**MARKS VERIFICATION**: Before outputting, VERIFY that the sum of all question marks equals EXACTLY ${params.totalMarks}. If not, adjust the marks to match.

For each generated question, provide the following fields in the JSON object:
- 'section': A string indicating the section name (e.g., "Section A", "Section B").
- 'question': The full formatted question text with a brief intro and clearly labeled sub-parts. Include sub-part marks in parentheses (e.g., "(3 marks)").
- 'marks': An integer representing the TOTAL marks for this question (sum of all sub-parts).
- 'type': The type of question ('Short Answer', 'Essay', or 'MCQ').
- 'difficulty': MUST be "${params.difficulty}" for ALL questions (this is the user's selected difficulty level - do NOT use any other value).
- 'answer': For non-MCQ, a structured string with answers labeled per sub-part. For MCQ, the letter of the correct option.
- 'explanation': A structured string with STEP-BY-STEP working for each sub-part. Each step should be numbered (Step 1, Step 2, etc).
- 'options': For MCQ questions, this MUST be an array of strings. For other question types, this field should be omitted.

The 'answer' and 'explanation' should be clear, well-structured, and correspond to each sub-part of the question to serve as a marking scheme.

Return the output as a JSON object matching the provided schema. The 'questions' array should contain a flat list of all question objects.
`;

  try {
    const jsonText = await geminiGenerate(prompt);
    console.log('Raw Gemini response:', jsonText); // Log the raw response for debugging

    // First verify we got a non-empty response
    if (!jsonText || typeof jsonText !== 'string' || jsonText.trim().length < 10) {
      console.error('Empty or invalid response from Gemini:', jsonText);
      throw new Error('Empty or invalid response from Gemini');
    }

    let result: { questions?: any[] };
    try {
      result = parseJsonFromText(jsonText);
      console.log('Parsed result:', result); // Log the parsed result for debugging
    } catch (e) {
      console.error('JSON parse error:', e, 'raw:', jsonText);
      // Try to recover by looking for any array in the response
      const arrayMatch = jsonText.match(/\[\s*\{[^\]]*\}\s*\]/);
      if (arrayMatch) {
        try {
          const questions = JSON.parse(arrayMatch[0]);
          result = { questions };
          console.log('Recovered questions from response:', questions);
        } catch (e2) {
          console.error('Recovery attempt failed:', e2);
          throw new Error('Failed to parse Gemini JSON response');
        }
      } else {
        throw new Error('Failed to parse Gemini JSON response');
      }
    }

    if (!result || !result.questions) {
      console.error('No questions object in response:', result);
      throw new Error('Invalid response format from Gemini');
    }

    if (!Array.isArray(result.questions) || result.questions.length === 0) {
      console.error('No questions array or empty questions:', result.questions);
      throw new Error('No questions in response');
    }

    // Process and validate each question
    const processedQuestions = result.questions.map((q: any) => {
      if (!q || typeof q !== 'object') {
        console.error('Invalid question object:', q);
        throw new Error('Invalid question format in response');
      }

      const question = {
        ...q,
        id: `gemini-${Date.now()}-${Math.random()}`,
        subject: params.subject,
        grade: 'N/A',
        topic: topicsString
      };

      // Validate and fix MCQ options
      if (q.type === 'MCQ') {
        // Ensure options array exists and has exactly 4 items
        if (!Array.isArray(q.options) || q.options.length !== 4) {
          console.error('Invalid MCQ options for question:', q);
          throw new Error('MCQ question must have exactly 4 options');
        }

        // Clean up and validate options
        question.options = q.options.map((opt: string, index: number) => {
          if (!opt || typeof opt !== 'string' || opt.trim().length === 0) {
            console.error('Empty or invalid option:', index, opt);
            throw new Error(`MCQ option ${index + 1} is empty or invalid`);
          }

          // Clean up the option format
          let cleanOption = opt.trim();

          // First remove all variations of answer letter prefixes
          const letterPatterns = [
            /^[A-Da-d]\.?\s*/g,                  // A. or A
            /^[A-Da-d]\)\s*/g,                   // A)
            /^[A-Da-d][\:\-\.]?\s*/g,           // A: or A- or A.
            /\b[A-Da-d]\.?\s+/g,                // Letter followed by space
            /^[A-Da-d][\.\)\:\-]?\s*[A-Da-d]\.?\s*/g,  // A. A. or A.A. or A) A.
            /\b[A-Da-d]\.\s*[A-Da-d]\.\s*/g     // Letter.Letter. anywhere
          ];

          letterPatterns.forEach(pattern => {
            cleanOption = cleanOption.replace(pattern, '');
          });

          // Clean up mathematical expressions
          cleanOption = cleanOption
            .replace(/theta/g, 'θ')
            .replace(/\bpi\b/g, 'π')
            .replace(/sqrt/g, '√')
            .replace(/\^(\d+)/g, (_, num) => ['²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'][num - 2] || `^${num}`)
            // Cleanup any remaining duplicate letters
            .replace(/\b[A-D]\.\s*[A-D]\.\s*/g, '')
            .replace(/\b[A-D]\.\s*/g, '')
            .trim();

          // Options should come with their own letter prefixes from Gemini
          return cleanOption;
        });

        // Ensure answer is a valid option letter and clean it
        if (!q.answer || !['A', 'B', 'C', 'D'].includes(q.answer.toUpperCase().replace(/[^A-D]/g, ''))) {
          console.error('Invalid MCQ answer:', q.answer);
          throw new Error('MCQ answer must be A, B, C, or D');
        }

        // Clean up the answer and ensure it's valid
        let answerLetter: string;
        let answerIndex: number;

        // First try to get the answer letter from the raw answer
        const rawAnswer = String(q.answer || '').toUpperCase();
        const letterMatch = rawAnswer.match(/[A-D]/);

        if (letterMatch) {
          // Found a valid letter (A, B, C, or D)
          answerLetter = letterMatch[0];
          answerIndex = answerLetter.charCodeAt(0) - 65;
        } else {
          // No valid letter found - default to 'A'
          console.warn('No valid answer letter found, defaulting to A');
          answerLetter = 'A';
          answerIndex = 0;
        }

        // Validate the answer index
        if (answerIndex < 0 || answerIndex >= question.options.length) {
          console.error('Invalid answer index:', answerIndex, 'options:', question.options);
          answerLetter = 'A';
          answerIndex = 0;
        }

        // Get the corresponding option text
        const answerText = question.options[answerIndex]
          .replace(/^[A-D]\.?\s*/g, '')    // Remove letter prefix
          .replace(/^[A-D]\)\s*/g, '')     // Remove letter) prefix
          .replace(/\b[A-D]\.\s*/g, '')    // Remove any remaining letter prefixes
          .trim();

        question.answer = `${answerLetter}. ${answerText}`;
      }

      return question;
    });

    // Log AI usage for exam generation
    await logAIUsage(
      'exams',
      currentModelName,
      prompt,
      jsonText,
      schoolId
    );

    return processedQuestions;
  } catch (err) {
    console.error('Gemini question generation failed:', err);
    // fallback to mock (same as before)
    return generateMockQuestions(params, content);
  }
};

/* -------------------------------------------------------------------------- */
/*  4b. generateQuestionsFromPDF – Uses SAME output format as generateQuestions */
/* -------------------------------------------------------------------------- */

export interface PDFQuestionParams extends GenerationParams {
  documentContent: string;
  documentTitle?: string;
}

export interface PDFQuestion extends Question {
  sourceReference?: string;
  isDocumentBased: boolean;
  options?: string[];
}

export const generateQuestionsFromPDF = async (
  params: PDFQuestionParams,
  schoolId?: string,
  isIndependent?: boolean
): Promise<PDFQuestion[]> => {
  const featureKey = isIndependent ? 'independent_examGeneration' : 'examGeneration';
  await initializeModel(featureKey, schoolId);

  const topicsString = params.topics.join(', ');

  const sectionPrompts = params.sections
    .map(section => {
      const isMCQ =
        (section as any).isMCQ === true ||
        String((section as any).questionType) === 'MCQ';
      let questionTypeDesc = '';
      if (isMCQ) {
        const optionsCount =
          typeof (section as any).numOptions === 'number' && (section as any).numOptions > 0
            ? (section as any).numOptions
            : 4;
        questionTypeDesc = `multiple-choice questions (MCQ). 
- Each question MUST have exactly ${optionsCount} options.
- The 'type' field in the JSON for these questions MUST be "MCQ".
- The 'options' field MUST be an array of strings containing the text for each option.
- The 'answer' field MUST be only the letter of the correct option (e.g., "C").`;
      } else {
        questionTypeDesc =
          section.questionType === 'Short Answer'
            ? 'short-answer, objective-style questions'
            : 'longer, essay-style questions requiring detailed explanations';
      }

      return `
- For ${section.name.toUpperCase()}:
  - Generate EXACTLY ${section.numQuestions} question(s) for this section.
  - The questions should be ${questionTypeDesc}.
  - Total marks for this section: ${section.marks} marks.
`;
    })
    .join('');

  const totalQuestions = params.sections.reduce((a, s) => a + s.numQuestions, 0);

  // Use EXACT same output format as generateQuestions
  const prompt = `
You are an expert curriculum developer specializing in creating high-quality exam papers for the Kenya Certificate of Secondary Education (KCSE). Your task is to generate exam questions in valid JSON format.

=== REFERENCE DOCUMENT ===
${params.documentContent}
=== END OF REFERENCE ===

Document Title: ${params.documentTitle || 'Exam Paper'}

CRITICAL: You must return a valid JSON object with this exact structure:
{
  "questions": [
    {
      "section": "Section A",
      "question": "The actual question text...",
      "marks": 5,
      "type": "MCQ",
      "difficulty": "${params.difficulty}",
      "answer": "A",
      "explanation": "Step-by-step solution...",
      "options": ["A. First option", "B. Second option", "C. Third option", "D. Fourth option"]
    }
  ]
}

Rules for valid response:
1. Must be pure JSON - no markdown, no text before or after
2. The 'questions' field must be an array with at least one question
3. Each question must have ALL required fields
4. MCQ questions must have exactly 4 options with letter prefixes
5. Use proper mathematical symbols (e.g., π, θ, √, ², m², cm³) instead of text (e.g., pi, theta, sqrt, squared, square meters, cubic cm).
6. No duplicate letters in options or answer fields

**=== MANDATORY CONFIGURATION (NON-NEGOTIABLE) ===**

The following settings have been configured by the teacher and MUST be respected EXACTLY:

- **Exam Title**: ${params.examTitle}
- **Exam Type**: ${params.examType}
- **Subject**: ${params.subject}
- **Topics**: ${params.topics.join(', ')}
- **Difficulty Level**: ${params.difficulty} (ALL questions MUST be this difficulty)
- **Total Marks**: ${params.totalMarks} (THIS IS CRITICAL - see below)
- **Number of Sections**: ${params.sections.length}

**=== MARKS DISTRIBUTION - ABSOLUTELY CRITICAL ===**

You MUST generate questions that sum to EXACTLY ${params.totalMarks} marks total.
- If you need ${totalQuestions} questions totaling ${params.totalMarks} marks, calculate: ${params.totalMarks} ÷ ${totalQuestions} = ${Math.floor(params.totalMarks / totalQuestions)} marks per question (approximately)
- Adjust individual question marks so they SUM to EXACTLY ${params.totalMarks}
- DO NOT generate questions with random marks that don't add up correctly

${sectionPrompts}

**VERIFICATION BEFORE OUTPUT**: 
1. Count total questions - must match section requirements EXACTLY
2. **SUM ALL MARKS** - must equal EXACTLY ${params.totalMarks} (NOT MORE, NOT LESS)
3. Verify all questions are "${params.difficulty}" difficulty
4. Verify subject is "${params.subject}"

${params.customInstructions ? `Additional instructions: "${params.customInstructions}"` : ''}


CRITICAL INSTRUCTIONS FOR CLEAR, TEACHER-FRIENDLY OUTPUT:

1. Write all questions and answers in simple, clear language:
   - Use everyday terms that all teachers can understand
   - Break down complex mathematics into simple steps
   - Avoid confusing mathematical symbols
   - Write explanations in complete sentences

2. For mathematical expressions:
   - Write equations in standard, readable form
   - Use proper mathematical symbols for units (e.g., cm², m³, units²). Use superscripts for powers (e.g., x², y³) instead of words like "squared" or "cubed".
   - Show each calculation step clearly
   - Include clear diagrams where needed

3. Format the marking scheme clearly:
   - **MAXIMUM 5 STEPS PER ANSWER** - Never exceed 5 steps
   - Each step must be concise and clear
   - Show marks for each part clearly [e.g., Step 1 (2 marks)]
   - Use simple language in explanations

Generate ${totalQuestions} total questions based on the following criteria:
- Subject: ${params.subject}
- Topic(s): ${topicsString}
- Difficulty Level: ${params.difficulty}

**ENFORCEMENT**: The difficulty level "${params.difficulty}" is MANDATORY. Every single question you generate MUST adhere strictly to the "${params.difficulty}" difficulty criteria defined above. If a question does not match the specified difficulty level, it will be rejected.

REMEMBER: Write everything so that ANY teacher can understand it, regardless of their mathematical background.

**CRITICAL: QUESTION STRUCTURE FORMAT**

**DO NOT use generic phrases like "Answer the following questions about [topic]:" - this sounds unnatural.**
Use NATURAL exam language like:
  - "Define the following:" then (a), (b), (c)...
  - "Find the following values:" then (a), (b)...
  - "Solve:" then (a), (b)...
  - Or just state context, then sub-parts

**CRITICAL ANSWER FORMAT RULES:**
1. The 'answer' field MUST contain the ACTUAL FINAL ANSWER, not "See steps below" or just "(a)"
2. For multi-part questions: "(a) [actual answer]\\n(b) [actual answer]"
3. NEVER put "See steps below" or "See sub-parts" in the answer field

**CRITICAL EXPLANATION FORMAT RULES:**
1. The 'explanation' field MUST contain step-by-step working
2. **MAXIMUM 5 STEPS** - combine steps if needed to stay within 5
3. Format: "(a) Step 1: ... Step 2: ... (max 5)\\n(b) Step 1: ... (max 5)"
4. Each step must show actual calculations/reasoning, not just describe what to do

**MARKS VERIFICATION**: Before outputting, VERIFY that the sum of all question marks equals EXACTLY ${params.totalMarks}. If not, adjust the marks to match.

For each generated question, provide the following fields in the JSON object:
- 'section': A string indicating the section name (e.g., "Section A", "Section B").
- 'question': The full formatted question text with a brief intro and clearly labeled sub-parts. Include sub-part marks in parentheses (e.g., "(3 marks)").
- 'marks': An integer representing the TOTAL marks for this question (sum of all sub-parts).
- 'type': The type of question ('Short Answer', 'Essay', or 'MCQ').
- 'difficulty': MUST be "${params.difficulty}" for ALL questions (this is the user's selected difficulty level - do NOT use any other value).
- 'answer': For non-MCQ, a structured string with answers labeled per sub-part. For MCQ, the letter of the correct option.
- 'explanation': A structured string with STEP-BY-STEP working for each sub-part. Each step should be numbered (Step 1, Step 2, etc).
- 'options': For MCQ questions, this MUST be an array of strings. For other question types, this field should be omitted.

The 'answer' and 'explanation' should be clear, well-structured, and correspond to each sub-part of the question to serve as a marking scheme.

Return the output as a JSON object matching the provided schema. The 'questions' array should contain a flat list of all question objects.
`;

  try {
    const jsonText = await geminiGenerate(prompt);
    console.log('Raw PDF Gemini response:', jsonText);

    if (!jsonText || typeof jsonText !== 'string' || jsonText.trim().length < 10) {
      console.error('Empty or invalid response from Gemini:', jsonText);
      throw new Error('Empty or invalid response from Gemini');
    }

    let result: { questions?: any[] };
    try {
      result = parseJsonFromText(jsonText);
      console.log('Parsed PDF result:', result);
    } catch (e) {
      console.error('JSON parse error:', e, 'raw:', jsonText);
      const arrayMatch = jsonText.match(/\[\s*\{[^\]]*\}\s*\]/);
      if (arrayMatch) {
        try {
          const questions = JSON.parse(arrayMatch[0]);
          result = { questions };
        } catch (e2) {
          throw new Error('Failed to parse Gemini JSON response');
        }
      } else {
        throw new Error('Failed to parse Gemini JSON response');
      }
    }

    if (!result || !result.questions) {
      console.error('No questions object in response:', result);
      throw new Error('Invalid response format from Gemini');
    }

    if (!Array.isArray(result.questions) || result.questions.length === 0) {
      console.error('No questions array or empty questions:', result.questions);
      throw new Error('No questions in response');
    }

    // Process questions - SAME logic as generateQuestions
    const processedQuestions: PDFQuestion[] = result.questions.map((q: any) => {
      if (!q || typeof q !== 'object') {
        console.error('Invalid question object:', q);
        throw new Error('Invalid question format in response');
      }

      const question: PDFQuestion = {
        ...q,
        id: `pdf-gemini-${Date.now()}-${Math.random()}`,
        subject: params.subject,
        grade: 'N/A',
        topic: topicsString,
        sourceReference: '[Document]',
        isDocumentBased: true
      };

      // Validate and fix MCQ options - SAME as generateQuestions
      if (q.type === 'MCQ') {
        if (!Array.isArray(q.options) || q.options.length !== 4) {
          console.error('Invalid MCQ options for question:', q);
          throw new Error('MCQ question must have exactly 4 options');
        }

        question.options = q.options.map((opt: string, index: number) => {
          if (!opt || typeof opt !== 'string' || opt.trim().length === 0) {
            console.error('Empty or invalid option:', index, opt);
            throw new Error(`MCQ option ${index + 1} is empty or invalid`);
          }

          let cleanOption = opt.trim();
          const letterPatterns = [
            /^[A-Da-d]\.?\s*/g,
            /^[A-Da-d]\)\s*/g,
            /^[A-Da-d][\:\-\.]?\s*/g,
            /\b[A-Da-d]\.?\s+/g,
            /^[A-Da-d][\.\ )\:\-]?\s*[A-Da-d]\.?\s*/g,
            /\b[A-Da-d]\.\s*[A-Da-d]\.\s*/g
          ];

          letterPatterns.forEach(pattern => {
            cleanOption = cleanOption.replace(pattern, '');
          });

          cleanOption = cleanOption
            .replace(/theta/g, 'θ')
            .replace(/\bpi\b/g, 'π')
            .replace(/sqrt/g, '√')
            .replace(/\^(\d+)/g, (_, num) => ['²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'][num - 2] || `^${num}`)
            .replace(/\b[A-D]\.\s*[A-D]\.\s*/g, '')
            .replace(/\b[A-D]\.\s*/g, '')
            .trim();

          return cleanOption;
        });

        if (!q.answer || !['A', 'B', 'C', 'D'].includes(q.answer.toUpperCase().replace(/[^A-D]/g, ''))) {
          console.error('Invalid MCQ answer:', q.answer);
          throw new Error('MCQ answer must be A, B, C, or D');
        }

        const rawAnswer = String(q.answer || '').toUpperCase();
        const letterMatch = rawAnswer.match(/[A-D]/);
        let answerLetter: string;
        let answerIndex: number;

        if (letterMatch) {
          answerLetter = letterMatch[0];
          answerIndex = answerLetter.charCodeAt(0) - 65;
        } else {
          answerLetter = 'A';
          answerIndex = 0;
        }

        if (answerIndex < 0 || !question.options || answerIndex >= question.options.length) {
          answerLetter = 'A';
          answerIndex = 0;
        }

        const answerText = question.options && question.options[answerIndex]
          ? question.options[answerIndex]
            .replace(/^[A-D]\.?\s*/g, '')
            .replace(/^[A-D]\)\s*/g, '')
            .replace(/\b[A-D]\.\s*/g, '')
            .trim()
          : '';

        question.answer = `${answerLetter}. ${answerText}`;
      }

      return question;
    });

    await logAIUsage(
      'exams',
      currentModelName,
      prompt,
      jsonText,
      schoolId
    );

    return processedQuestions;
  } catch (err) {
    console.error('PDF question generation failed:', err);
    throw err;
  }
};

/* -------------------------------------------------------------------------- */
/*  5. Research – uses Gemini with Google Search grounding (no key)          */
/* -------------------------------------------------------------------------- */
const performMockResearch = (subject: string, topics: string[], areaOfFocus: string): ResearchResult => {
  /* … (exact same mock you already have) … */
  const topicsString = topics.join(', ');
  const technicalSubjects = ['mathematics', 'physics', 'chemistry', 'biology'];
  const isTechnical = technicalSubjects.includes(subject.toLowerCase());

  if (isTechnical) {
    return {
      summary: `This mock research provides a practical, example-driven overview of ${topicsString} in ${subject}. The focus is exclusively on the application of formulas and problem-solving techniques, with special attention to ${areaOfFocus || 'the core concepts'}.`,
      categories: [
        {
          title: "Concept 1: Integration by Parts",
          content: `The formula for Integration by Parts is derived from the product rule for differentiation. It is given by:
$$ \\int u \\, dv = uv - \\int v \\, du $$
**Example:** Calculate $ \\int x \\cos(x) \\, dx $.
1.  **Identify u and dv:** Let $ u = x $ and $ dv = \\cos(x) \\, dx $.
2.  **Find du and v:** Differentiating $u$ gives $ du = dx $. Integrating $dv$ gives $ v = \\sin(x) $.
3.  **Apply the formula:**
$$ \\int x \\cos(x) \\, dx = x \\sin(x) - \\int \\sin(x) \\, dx $$
4.  **Solve the remaining integral:**
$$ \\int \\sin(x) \\, dx = -\\cos(x) + C $$
5.  **Combine the results:**
$$ \\int x \\cos(x) \\, dx = x \\sin(x) + \\cos(x) + C $$`,
        },
        {
          title: "Concept 2: First-Order Linear Differential Equations",
          content: `A first-order linear differential equation has the form:
$$ \\frac{dy}{dx} + P(x)y = Q(x) $$
The solution is found using an integrating factor, $ I(x) $, where $ I(x) = e^{\\int P(x) \\, dx} $.
The general solution is $ y \\cdot I(x) = \\int Q(x) I(x) \\, dx $.
**Example:** Solve $ \\frac{dy}{dx} + 2y = e^x $.
1.  **Identify P(x) and Q(x):** Here, $ P(x) = 2 $ and $ Q(x) = e^x $.
2.  **Calculate the integrating factor I(x):**
$$ I(x) = e^{\\int 2 \\, dx} = e^{2x} $$
3.  **Apply the general solution formula:**
$$ y \\cdot e^{2x} = \\int e^x \\cdot e^{2x} \\, dx = \\int e^{3x} \\, dx $$
4.  **Solve the integral:**
$$ \\int e^{3x} \\, dx = \\frac{1}{3}e^{3x} + C $$
5.  **Isolate y for the final solution:**
$$ y \\cdot e^{2x} = \\frac{1}{3}e^{3x} + C $$
$$ y = \\frac{1}{3}e^x + Ce^{-2x} $$`,
        },
      ],
      sources: [
        { web: { uri: "https://example.com/mock-source-1", title: "Mock Source: Practical Calculus" } },
        { web: { uri: "https://example.com/mock-source-2", title: "Mock Source: Solving Differential Equations" } },
      ],
    };
  }

  return {
    summary: `This is a mock research summary for the topic(s) of ${topicsString} within the subject of ${subject}. It provides a high-level overview of the subject matter, written in a smooth, continuous prose style that emulates an academic article. The purpose is to introduce the main themes before they are explored in greater detail in the subsequent categorized sections.`,
    categories: [
      {
        title: "Key Concepts",
        content: `The fundamental ideas related to ${topicsString} are central to its understanding. This first paragraph introduces these core principles, laying a foundation for more complex discussions.\n\nFurther elaboration on these principles is presented in this second paragraph. It connects the foundational ideas to broader theoretical frameworks, demonstrating how they fit within the larger academic discourse on the subject. This approach ensures a comprehensive, book-like presentation of the information.`,
      },
      {
        title: "Historical Context",
        content: `To fully appreciate the current state of ${topicsString}, it is necessary to examine its historical development. The origins of the field can be traced back to the early twentieth century, with seminal work contributed by several pioneers. This historical perspective provides context for the evolution of its key concepts and applications.`,
      },
    ],
    sources: [
      { web: { uri: "https://example.com/mock-source-1", title: "Mock Source One: An Overview" } },
      { web: { uri: "https://example.com/mock-source-2", title: "Mock Source Two: In-depth Analysis" } },
    ],
  };
};

export const performResearch = async (
  subject: string,
  topics: string[],
  areaOfFocus: string,
  schoolId?: string,
  isIndependent?: boolean
): Promise<ResearchResult> => {
  // Initialize model with research configuration
  const featureKey = isIndependent ? 'independent_research' : 'research';
  await initializeModel(featureKey, schoolId);

  const topicsString = topics.join(', ');
  const technicalSubjects = ['mathematics', 'physics', 'chemistry', 'biology'];
  const isTechnical = technicalSubjects.includes(subject.toLowerCase());

  const focusInstruction = areaOfFocus
    ? `A specific "Area of Focus" has been provided by the user. You must heavily emphasize this aspect throughout your response, dedicating significant detail to it in your explanations and examples. The area of focus is: "${areaOfFocus}".`
    : '';

  const technicalInstruction = isTechnical
    ? `
**CRITICAL INSTRUCTIONS FOR TECHNICAL SUBJECTS (MATHEMATICS, PHYSICS, etc.)**

You are an AI expert tasked with creating practical, problem-solving tutorials, NOT historical essays. Your primary function is to demonstrate concepts through application.

**CORE DIRECTIVE: Prioritize formulas, calculations, and worked examples above all else.**

**STRICT RULES YOU MUST FOLLOW:**
1.  **NO HISTORY or BIOGRAPHIES:** You MUST NOT provide historical background, biographical details of mathematicians/physicists, or lengthy theoretical discussions about the origins of concepts. The user wants a practical guide, not a history lesson.
2.  **EXAMPLE-DRIVEN CONTENT:** For EVERY single concept or formula introduced, you MUST immediately follow it with at least one clear, step-by-step worked example showing how to apply it. The majority of your output should be these examples.
3.  **FORMULA-First APPROACH:** State the formula clearly before showing how it's used.
4.  **PROFESSIONAL LaTeX FORMATTING:** All mathematical content (variables, formulas, equations) MUST be formatted in proper LaTeX. Use \`$$...$$\` for block-level equations to ensure they are on their own line with proper spacing. Use \`$...$\` for inline math.

The final output must be a highly practical, educational resource focused entirely on a "how-to" approach, where theory is only present to introduce a problem that an example will then solve.`
    : '';

  const researchPrompt = `
You are an expert academic writer. Generate a comprehensive research report on the following topic(s): "${topicsString}", within the subject of "${subject}". The report must be grounded in real-time web search results.

Write the entire report in the formal style of a research book or academic article. The text must consist of smooth, well-structured paragraphs.

Crucially, you must not use any markdown formatting. This means no headings (like ##), no bullet points (like * or -), no numbered lists, no bold text (like **text**), and no special symbols for visual formatting.

${focusInstruction}
${technicalInstruction}

Emphasize key information naturally within the narrative through sophisticated wording and sentence structure. The final output should be a single block of professionally written text.
`;

  try {
    // First call – get grounded text + sources
    const groundingText = await geminiGenerate(researchPrompt);

    // Firebase does not expose groundingChunks directly yet – we ask Gemini to return them in JSON
    const sourcesPrompt = `
You are a helper that extracts the grounding sources from the previous response.
Return ONLY a JSON array of objects with shape:
[{ "web": { "uri": string, "title": string } }, ...]

If no specific web sources are cited or found in the text, return an empty array: []
Do not provide any explanations.
`;

    const sourcesJson = await geminiGenerate(`${groundingText}\n\n${sourcesPrompt}`);
    let sources: GroundingChunk[] = [];
    try {
      const parsed = parseJsonFromText(sourcesJson);
      if (Array.isArray(parsed)) {
        // Validate items
        sources = parsed.filter(item => item && item.web && typeof item.web.uri === 'string' && typeof item.web.title === 'string');
      } else {
        console.warn('Parsed sources is not an array:', parsed);
        sources = [];
      }
    } catch (e) {
      console.warn('Failed to parse grounding sources JSON:', e, 'raw:', sourcesJson);
      sources = [];
    }

    // Second call – categorize the grounded text
    const categorizationPrompt = `
You are an expert at structuring research documents. Analyze the following academic text about "${topicsString}" in the subject of "${subject}". Your task is to:

1. Generate a concise, high-level summary of the entire text. This summary must be a single, well-written paragraph in a formal academic style.
2. Identify the main themes in the text and organize the content into distinct categories.
3. For each category, provide a concise, descriptive title. The content for each category must be the relevant portion of the original text, presented in smooth, well-structured paragraphs, maintaining the academic tone and preserving all original LaTeX formatting. Do not add any new formatting like markdown or bullets.

--- ACADEMIC TEXT ---
${groundingText}
--- END OF TEXT ---

Return the output as a JSON object that matches the provided schema, containing both the summary and the array of categories.
`;

    const catJson = await geminiGenerate(categorizationPrompt);
    let parsedCat: any;
    try {
      parsedCat = parseJsonFromText(catJson);
    } catch (e) {
      console.error('Failed to parse categorization JSON:', e, 'raw:', catJson);
      throw e;
    }
    const { summary, categories } = parsedCat;

    // Log AI usage for research feature
    await logAIUsage(
      'research',
      currentModelName,
      researchPrompt + categorizationPrompt,
      groundingText + catJson,
      schoolId
    );

    return { summary, categories, sources };
  } catch (err) {
    console.error('Research failed:', err);
    return performMockResearch(subject, topics, areaOfFocus);
  }
};

/* -------------------------------------------------------------------------- */
/*  6. Book details – HIGHLY OPTIMIZED for accuracy and multiple results     */
/* -------------------------------------------------------------------------- */

// Helper: Normalize text for fuzzy matching
function normalizeText(text: string): string {
  return String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[''`]/g, "'")
    .replace(/[""]/g, '"')
    .replace(/\s+/g, ' ');
}

// Helper: Calculate similarity score
function similarityScore(str1: string, str2: string): number {
  const s1 = normalizeText(str1);
  const s2 = normalizeText(str2);
  if (s1 === s2) return 1;
  if (!s1 || !s2) return 0;

  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;

  if (longer.includes(shorter)) return 0.9;

  // Word overlap check
  const words1 = s1.split(' ').filter(w => w.length > 2);
  const words2 = s2.split(' ').filter(w => w.length > 2);
  const commonWords = words1.filter(w => words2.some(w2 => w2.includes(w) || w.includes(w2)));

  return commonWords.length / Math.max(words1.length, words2.length, 1);
}

// Google Books API search (free, no auth required, better for African literature)
async function searchGoogleBooks(title: string, author: string): Promise<BookDetails[]> {
  const results: BookDetails[] = [];

  try {
    // Build query - try multiple strategies
    let queries: string[] = [];

    // Strategy 1: Combined title + author
    if (title && author) {
      queries.push(`intitle:${encodeURIComponent(title)}+inauthor:${encodeURIComponent(author)}`);
    }
    // Strategy 2: Just title (more flexible)
    if (title) {
      queries.push(`intitle:${encodeURIComponent(title)}`);
    }
    // Strategy 3: General search
    const combined = [title, author].filter(Boolean).join(' ');
    if (combined) {
      queries.push(encodeURIComponent(combined));
    }

    for (const q of queries) {
      if (results.length >= 3) break;

      try {
        const url = `https://www.googleapis.com/books/v1/volumes?q=${q}&maxResults=5&printType=books`;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(url, {
          signal: controller.signal,
          headers: { 'Accept': 'application/json' }
        });
        clearTimeout(timeoutId);

        if (!response.ok) continue;

        const data = await response.json();

        if (!data.items || data.items.length === 0) continue;

        for (const item of data.items) {
          if (results.length >= 5) break;

          const info = item.volumeInfo || {};
          const bookTitle = info.title || '';
          const bookAuthors = info.authors || [];
          const bookAuthor = bookAuthors.join(', ') || 'Unknown Author';
          const year = info.publishedDate?.substring(0, 4) || 'Unknown';
          const description = info.description || '';
          const categories = info.categories || [];

          // Skip if already have this book
          if (results.some(r => normalizeText(r.title) === normalizeText(bookTitle))) continue;

          // Calculate relevance score
          const titleScore = similarityScore(title, bookTitle);
          const authorScore = author ? similarityScore(author, bookAuthor) : 0.5;
          const score = (titleScore * 0.7) + (authorScore * 0.3);

          if (score > 0.2 || !author) { // Lower threshold for partial matches
            results.push({
              title: bookTitle,
              author: bookAuthor,
              yearPublished: year,
              description: description.length > 300 ? description.substring(0, 300) + '...' : description || `A book by ${bookAuthor}`,
              category: categories[0] || 'General'
            });
          }
        }
      } catch {
        // Continue to next query strategy
      }
    }
  } catch (err) {
    console.log('Google Books lookup failed:', err);
  }

  return results.slice(0, 5);
}

// Open Library API search
async function searchOpenLibrary(title: string, author: string): Promise<BookDetails[]> {
  const results: BookDetails[] = [];

  try {
    // Try multiple search strategies
    const queries: string[] = [];

    // Strategy 1: Title search only (more reliable)
    if (title) {
      queries.push(`title=${encodeURIComponent(title)}`);
    }
    // Strategy 2: Combined search
    const combined = [title, author].filter(Boolean).join(' ');
    if (combined) {
      queries.push(`q=${encodeURIComponent(combined)}`);
    }

    for (const queryString of queries) {
      if (results.length >= 3) break;

      try {
        const url = `https://openlibrary.org/search.json?${queryString}&limit=5&fields=title,author_name,first_publish_year,subject,key`;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(url, {
          signal: controller.signal,
          headers: { 'Accept': 'application/json' }
        });
        clearTimeout(timeoutId);

        if (!response.ok) continue;

        const data = await response.json();

        if (!data.docs || data.docs.length === 0) continue;

        for (const doc of data.docs) {
          if (results.length >= 5) break;

          const bookTitle = doc.title || '';
          const bookAuthor = doc.author_name?.[0] || 'Unknown Author';
          const year = doc.first_publish_year?.toString() || 'Unknown';
          const subjects = doc.subject?.slice(0, 3) || [];

          // Skip duplicates
          if (results.some(r => normalizeText(r.title) === normalizeText(bookTitle))) continue;

          const titleScore = similarityScore(title, bookTitle);
          if (titleScore > 0.2 || !title) {
            results.push({
              title: bookTitle,
              author: bookAuthor,
              yearPublished: year,
              description: '',
              category: subjects[0] || 'General'
            });
          }
        }
      } catch {
        // Continue to next strategy
      }
    }

    // Fetch descriptions in parallel
    const enrichPromises = results.map(async (result) => {
      try {
        // Search for the work key to get description
        const searchUrl = `https://openlibrary.org/search.json?title=${encodeURIComponent(result.title)}&limit=1&fields=key`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);

        const searchResp = await fetch(searchUrl, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (searchResp.ok) {
          const searchData = await searchResp.json();
          if (searchData.docs?.[0]?.key) {
            const workResp = await fetch(`https://openlibrary.org${searchData.docs[0].key}.json`);
            if (workResp.ok) {
              const workData = await workResp.json();
              if (workData.description) {
                result.description = typeof workData.description === 'string'
                  ? workData.description
                  : workData.description.value || '';
              }
            }
          }
        }
      } catch {
        // Ignore errors
      }
      if (!result.description) {
        result.description = `A book by ${result.author}`;
      }
      return result;
    });

    await Promise.all(enrichPromises);

  } catch (err) {
    console.log('Open Library lookup failed:', err);
  }

  return results;
}

// Combine results from multiple sources, deduplicated
function combineAndDedupeResults(
  googleResults: BookDetails[],
  openLibResults: BookDetails[],
  inputTitle: string,
  inputAuthor: string
): BookDetails[] {
  const combined: BookDetails[] = [];
  const seen = new Set<string>();

  // Score and combine all results
  const allResults = [...googleResults, ...openLibResults].map(book => {
    const titleScore = similarityScore(inputTitle, book.title);
    const authorScore = inputAuthor ? similarityScore(inputAuthor, book.author) : 0.5;
    return {
      ...book,
      relevanceScore: (titleScore * 0.6) + (authorScore * 0.4)
    };
  });

  // Sort by relevance
  allResults.sort((a, b) => b.relevanceScore - a.relevanceScore);

  // Deduplicate by normalized title
  for (const book of allResults) {
    const key = normalizeText(book.title);
    if (!seen.has(key)) {
      seen.add(key);
      combined.push({
        title: book.title,
        author: book.author,
        yearPublished: book.yearPublished,
        description: book.description,
        category: book.category
      });
    }
    if (combined.length >= 5) break;
  }

  return combined;
}

// Enrich books with AI-generated descriptions when they're too short
async function enrichDescriptions(books: BookDetails[]): Promise<BookDetails[]> {
  // Find books that need better descriptions (less than 50 chars or generic)
  const needsEnrichment = books.filter(book => {
    const desc = book.description.toLowerCase();
    return book.description.length < 50 ||
      desc === 'a novel' ||
      desc.startsWith('a book by') ||
      desc === 'unknown' ||
      desc === '';
  });

  if (needsEnrichment.length === 0) {
    return books; // All descriptions are fine
  }

  console.log(`Enriching descriptions for ${needsEnrichment.length} books...`);

  try {
    await initializeModel('libraryManagement');

    // Generate descriptions in parallel for speed
    const enrichPromises = needsEnrichment.map(async (book) => {
      try {
        const descPrompt = `Write a 2-3 sentence description/summary for this book:
Title: "${book.title}"
Author: "${book.author}"
Year: "${book.yearPublished}"
Category: "${book.category}"

Rules:
- Be factual and accurate about the book's plot/content
- Do not make up details if you don't know the book
- If it's a Swahili/African novel, describe its themes accurately
- Keep it under 200 words
- Return ONLY the description text, no quotes or labels`;

        const description = await geminiGenerate(descPrompt);

        // Clean the description
        let cleanDesc = description.trim()
          .replace(/^["']|["']$/g, '') // Remove surrounding quotes
          .replace(/^Description:\s*/i, '') // Remove "Description:" prefix
          .replace(/^Summary:\s*/i, ''); // Remove "Summary:" prefix

        if (cleanDesc.length > 20) {
          book.description = cleanDesc.length > 500 ? cleanDesc.substring(0, 500) + '...' : cleanDesc;
        }
      } catch (err) {
        console.log(`Failed to enrich description for "${book.title}":`, err);
        // Keep original description
      }
      return book;
    });

    await Promise.all(enrichPromises);

  } catch (err) {
    console.log('Description enrichment failed:', err);
    // Return books with original descriptions
  }

  return books;
}

export const generateBookDetails = async (
  title: string,
  author: string,
  categories: string[],
  schoolId?: string
): Promise<BookDetails[]> => {

  // ============================================================
  // CHECK: Verify AI features are not paused (globally or for this school)
  // ============================================================
  const isPaused = await isSchoolAIPaused(schoolId);
  if (isPaused) {
    throw new Error('AI features are currently paused. Please contact your administrator.');
  }

  console.log(`Searching for book: "${title}" by "${author}"`);

  const normalizedTitle = normalizeText(title);

  // ============================================================
  // STEP 1: Detect if this looks like Swahili/African literature
  // For these, APIs often have incomplete data, so use Gemini directly
  // ============================================================
  const swahiliIndicators = ['la', 'ya', 'wa', 'na', 'ni', 'kwa', 'mtu', 'nyumba', 'bwana', 'bibi', 'chozi', 'heri', 'kidagaa', 'tumbo'];
  const titleWords = normalizedTitle.split(' ');
  const looksLikeSwahili = titleWords.some(word => swahiliIndicators.includes(word));

  if (looksLikeSwahili) {
    console.log('Detected Swahili/African title, using Gemini directly for better accuracy...');
    return await searchWithGemini(title, author, categories, schoolId);
  }

  // ============================================================
  // STEP 2: Search APIs for international books
  // ============================================================
  const [googleResults, openLibResults] = await Promise.all([
    searchGoogleBooks(title, author),
    searchOpenLibrary(title, author)
  ]);

  console.log(`Google Books found: ${googleResults.length}, Open Library found: ${openLibResults.length}`);

  // Combine and deduplicate results
  let apiResults = combineAndDedupeResults(googleResults, openLibResults, title, author);

  // CRITICAL: Filter out results with "Unknown Author" - they're useless
  apiResults = apiResults.filter(book => {
    const authorLower = book.author.toLowerCase();
    return authorLower !== 'unknown author' &&
      authorLower !== 'unknown' &&
      book.author.length > 2;
  });

  // If we have good results from APIs, enrich descriptions and return
  if (apiResults.length > 0) {
    console.log(`Found ${apiResults.length} quality results from book APIs`);

    // Enrich descriptions if they're too short
    apiResults = await enrichDescriptions(apiResults);

    console.log(`Returning ${apiResults.length} results with enriched descriptions`);
    return apiResults;
  }

  // ============================================================
  // STEP 3: Fall back to Gemini for everything else
  // ============================================================
  console.log('No quality API results, using Gemini AI...');
  return await searchWithGemini(title, author, categories, schoolId);
};

// Dedicated Gemini search function with rich descriptions
async function searchWithGemini(
  title: string,
  author: string,
  categories: string[],
  schoolId?: string
): Promise<BookDetails[]> {

  await initializeModel('libraryManagement', schoolId);

  const geminiPrompt = `You are an expert librarian with extensive knowledge of world literature, including African, Swahili, and East African literature (KCSE/KCPE setbooks from Kenya and Tanzania).

TASK: Find the REAL book(s) matching this search and provide COMPLETE information:
- Title (may have typos): "${title}"
- Author (may be partial or misspelled): "${author || 'not specified'}"

CRITICAL REQUIREMENTS:
1. ONLY return books that ACTUALLY EXIST - absolutely NO hallucinations or made-up books!
2. If you recognize a typo in the title or author name, CORRECT it to the real spelling
3. Include the CORRECT, VERIFIED author name
4. Provide the ACTUAL first publication year (if unsure, write "Unknown")
5. Write a DETAILED, ACCURATE description:
   - 3-5 sentences about the book's plot, main characters, and themes
   - Include the book's significance or why it's notable
   - For African/Swahili books, mention if it's a KCSE setbook
6. If the author name given is WRONG, return the CORRECT author name

IMPORTANT FOR ACCURACY:
- Double-check the author name before returning
- If the user provides a wrong author name, IGNORE it and return the correct one
- For Swahili titles, ensure you know the actual author from your training data

Return a JSON array with 1-3 best matches (ordered by relevance):
[
  {
    "title": "Correct Full Book Title",
    "author": "Correct Full Author Name",
    "yearPublished": "YYYY",
    "description": "Detailed 3-5 sentence description with plot summary and themes.",
    "category": "Fiction/Drama/Poetry/Non-Fiction"
  }
]

Preferred categories: ${categories.join(', ')}
If you cannot find ANY real book matching this search, return exactly: []`;

  try {
    const jsonText = await geminiGenerate(geminiPrompt);

    let cleanText = jsonText.trim();
    cleanText = cleanText.replace(/```json\s*/gi, '').replace(/```\s*/g, '');

    const arrayMatch = cleanText.match(/\[[\s\S]*\]/);
    if (arrayMatch) {
      cleanText = arrayMatch[0];
    }

    let results: BookDetails[];
    try {
      results = JSON.parse(cleanText);
    } catch {
      cleanText = cleanText
        .replace(/,\s*]/g, ']')
        .replace(/,\s*}/g, '}')
        .replace(/'/g, '"');
      results = JSON.parse(cleanText);
    }

    if (!Array.isArray(results) || results.length === 0) {
      throw new Error('No books found');
    }

    // Validate and clean results
    const validated = results
      .filter(book => {
        if (!book.title || book.title.length < 2) return false;
        if (!book.author || book.author.length < 2) return false;
        if (book.author.toLowerCase() === 'unknown') return false;
        return true;
      })
      .map(book => ({
        title: String(book.title || '').trim(),
        author: String(book.author || '').trim(),
        yearPublished: String(book.yearPublished || 'Unknown').trim(),
        description: String(book.description || '').trim(),
        category: String(book.category || 'General').trim()
      }))
      .slice(0, 5);

    if (validated.length > 0) {
      // Log AI usage for library management
      await logAIUsage(
        'library',
        currentModelName,
        geminiPrompt,
        jsonText,
        schoolId
      );

      return validated;
    }

    throw new Error('All results filtered out');

  } catch (err) {
    console.error('Gemini book lookup failed:', err);

    return [{
      title: title || 'Unknown Title',
      author: author || 'Unknown Author',
      description: `Could not find book details automatically. Please enter the information manually.`,
      category: 'General',
      yearPublished: 'Unknown'
    }];
  }
}

/* -------------------------------------------------------------------------- */
/*  7. Timetable – unchanged logic, now via Firebase Gemini                 */
/* -------------------------------------------------------------------------- */
export const generateTimetable = async (
  prompt: string
): Promise<string> => {
  try {
    const jsonText = await geminiGenerate(prompt);

    // basic validation
    JSON.parse(jsonText);
    return jsonText;
  } catch (err) {
    console.error('Timetable generation failed:', err);
    throw new Error(`Failed to generate timetable: ${err}`);
  }
};

/* -------------------------------------------------------------------------- */
/*  8. Citation Generation – Multiple academic formats                       */
/* -------------------------------------------------------------------------- */
export const generateCitation = async (
  source: GroundingChunk,
  format: 'APA' | 'MLA' | 'Chicago' | 'Harvard'
): Promise<string> => {
  const citationPrompt = `
Generate a ${format} format citation for the following web source:
Title: ${source.web.title}
URL: ${source.web.uri}

Return ONLY the formatted citation string, nothing else.
`;

  try {
    const citation = await geminiGenerate(citationPrompt);
    return citation.trim();
  } catch (err) {
    console.error('Citation generation failed:', err);
    // Fallback to basic format
    return `${source.web.title}. Retrieved from ${source.web.uri}`;
  }
};

/* -------------------------------------------------------------------------- */
/*  9. Plagiarism Detection – Content originality check                      */
/* -------------------------------------------------------------------------- */
export const checkPlagiarism = async (content: string): Promise<{ score: number; details: string }> => {
  const plagiarismPrompt = `
Analyze the following text for originality and potential plagiarism indicators.
Provide a score from 0-100 where 0 is completely plagiarized and 100 is completely original.
Also provide a brief explanation.

Text to analyze:
${content}

Return a JSON object with this structure:
{
  "score": number,
  "details": string
}
`;

  try {
    const result = await geminiGenerate(plagiarismPrompt);
    return parseJsonFromText(result);
  } catch (err) {
    console.error('Plagiarism check failed:', err);
    return { score: 50, details: 'Unable to perform plagiarism check. Please verify content manually.' };
  }
};

/* -------------------------------------------------------------------------- */
/*  10. Source Credibility Analysis                                          */
/* -------------------------------------------------------------------------- */
export const analyzeSourceCredibility = async (source: GroundingChunk): Promise<{ score: number; reasoning: string }> => {
  const credibilityPrompt = `
Analyze the credibility of this web source:
Title: ${source.web.title}
URL: ${source.web.uri}

Provide a credibility score from 0-100 and brief reasoning.
Return JSON: { "score": number, "reasoning": string }
`;

  try {
    const result = await geminiGenerate(credibilityPrompt);
    return parseJsonFromText(result);
  } catch (err) {
    console.error('Credibility analysis failed:', err);
    return { score: 50, reasoning: 'Unable to assess credibility automatically.' };
  }
};

/* -------------------------------------------------------------------------- */
/*  11. Content Summarization – Generate condensed versions                  */
/* -------------------------------------------------------------------------- */
export const generateSummary = async (content: string, maxWords: number = 100): Promise<string> => {
  const summaryPrompt = `
Create a concise summary of the following text in approximately ${maxWords} words.
Maintain key information and academic tone.

Text:
${content}

Return only the summary text.
`;

  try {
    return await geminiGenerate(summaryPrompt);
  } catch (err) {
    console.error('Summary generation failed:', err);
    return content.substring(0, maxWords * 5) + '...';
  }
};

/* -------------------------------------------------------------------------- */
/*  12. Related Topics Suggestion                                            */
/* -------------------------------------------------------------------------- */
export const suggestRelatedTopics = async (subject: string, topics: string[]): Promise<string[]> => {
  const suggestionPrompt = `
Based on the subject "${subject}" and topics "${topics.join(', ')}",
suggest 5 related research topics that would be valuable to explore.

Return as a JSON array of strings: ["topic1", "topic2", ...]
`;

  try {
    const result = await geminiGenerate(suggestionPrompt);
    return parseJsonFromText(result);
  } catch (err) {
    console.error('Related topics suggestion failed:', err);
    return [];
  }
};